import { Service, type Signal, computed, signal } from '@angular/core';
import type { WorkflowInstanceLog } from '@shared/models/workflow.model';
import type { ExpiringCredItem, ExpiringCredListResponse } from '../models/expiring-cred.model';
import type { VendorProfileData } from '../models/vendor-profile.model';
import type { ProfileWorkflowInstance } from '../models/vendor-registration-profile.model';
import {
  FORM_SUBMISSION_STEP_NAME,
  WORKFLOW_RESTARTED_DESC,
  isCurrentWorkflowStepName,
  isWorkflowEmpty,
} from '../utils/workflow-step.util';

/**
 * Aksi **Ajukan Pengajuan** — dimiliki shell, dirender tab Dokumen Perusahaan.
 *
 * `disabled` adalah signal milik shell (`!canSubmit`), diteruskan apa adanya: syaratnya tetap
 * dihitung di SATU tempat dan tab tidak bisa menurunkan versinya sendiri. Tampil/tidaknya tombol
 * BUKAN urusan aksi ini — itu `VendorProfileStore.submitVisible`.
 */
export interface SubmitAction {
  readonly disabled: Signal<boolean>;
  /** Syarat "siap diajukan" penuh (`canSubmit` shell) — untuk label/hint kartu progres. */
  readonly ready: Signal<boolean>;
  /** Alasan tombol mati yang perlu ditulis hint kartu progres; `null` = tidak ada. */
  readonly blocker: Signal<SubmitBlocker>;
  readonly run: () => void;
}

/**
 * Alasan tombol Ajukan Pengajuan mati padahal panel wajib sudah lengkap: belum ada perubahan data
 * yang diajukan, atau Ketentuan PDP belum disetujui.
 */
export type SubmitBlocker = 'noChanges' | 'pdp' | null;

/**
 * State halaman Vendor Profile, dibagi dari shell ke komponen tab.
 *
 * `autoProvided: false` + di-provide di route shell (`vendor.routes.ts`): datanya milik halaman
 * ini, hidup selama shell aktif dan ikut mati saat user pergi — bukan singleton root yang bisa
 * basi di sesi berikutnya.
 *
 * Ini menggantikan input binding: `provideRouter()` app ini tidak memakai
 * `withComponentInputBinding()`, dan menyalakannya akan mengubah perilaku SEMUA route
 * (plans/vendors/profile/PLAN.md §2i, §9b).
 *
 * Penulis HANYA shell; tab cuma membaca `profile` — supaya tidak ada dua sumber kebenaran.
 */
@Service({ autoProvided: false })
export class VendorProfileStore {
  private readonly data = signal<VendorProfileData | null>(null);
  private readonly creds = signal<readonly ExpiringCredItem[]>([]);
  private readonly credTotal = signal<number>(0);
  private readonly formId = signal<string | null>(null);
  private readonly slkId = signal<string | null>(null);
  private readonly loadedFlag = signal<boolean>(false);
  private readonly submit = signal<SubmitAction | null>(null);
  /** `undefined` = belum/gagal dimuat — BUKAN objek kosong BE (alur belum dimulai). */
  private readonly workflow = signal<ProfileWorkflowInstance | null | undefined>(undefined);
  private readonly logs = signal<readonly WorkflowInstanceLog[]>([]);

  readonly profile = this.data.asReadonly();

  /**
   * Dokumen kedaluwarsa milik vendor ini. Disimpan UTUH (bukan cuma jumlahnya) karena endpoint
   * yang sama adalah kandidat sumber data tab Expired Doc Status — supaya tab itu nanti tidak
   * perlu fetch ulang, sama seperti `profile`.
   */
  readonly expiringCreds = this.creds.asReadonly();

  /**
   * `total_server_items` dari BE — INI yang menjadi gerbang status SUSPEND, bukan
   * `expiringCreds().length`: endpoint `expiring_creds` ter-paginasi, jadi `items` hanya berisi
   * satu halaman dan panjangnya BUKAN jumlah sebenarnya.
   */
  readonly expiringCredTotal = this.credTotal.asReadonly();

  /**
   * `vendor_registration_form.id` dari `GET /api/vendor/registration/profiles/current_user` —
   * penyaring `workflow_instance_logs`. Sejak 6 Okt 2026 request itu dilakukan shell sendiri
   * (lihat `workflowLogs`), jadi tab Riwayat Komentar tidak lagi membaca id ini.
   *
   * `null` punya TIGA arti yang tidak bisa dibedakan dari nilainya sendiri: belum dimuat, gagal
   * dimuat, atau vendor memang belum punya formulir pendaftaran. Pembedanya `loaded`.
   *
   * BUKAN sinonim `profile()?.vendor_registration?.id` — itu id PENDAFTARAN, bukan id FORMULIR
   * pendaftaran (PLAN-COMMENT-HISTORY-TAB.md D2). Jangan saling menggantikan.
   */
  readonly registrationFormId = this.formId.asReadonly();

  /**
   * Vendor ini SUDAH punya SLK (`last_vendor_slk.id` dari
   * `GET /api/vendor/registration/profiles/current_user`) → baris "Active TDPM No" merender nomor
   * + tombol cetak. Tanpa itu: '-' polos, TANPA tombol.
   *
   * Gerbangnya SLK, **bukan tahap rekanan**. `current_user` tidak mengirim tahap sama sekali —
   * `vendor_type.ident_name` berisi `STANDARD`, sedangkan yang membedakan Candidate dari Vendor
   * adalah `vendor_registration_state` yang hanya ada di endpoint list sisi admin. Lagi pula SLK
   * adalah gerbang yang LEBIH TEPAT: ia persis mencerminkan penolakan BE
   * ("Vendor registration SLK letter has not been issued"), sehingga vendor penuh yang SLK-nya
   * belum terbit pun tidak ditawari tombol yang pasti gagal.
   *
   * Arah gagalnya disengaja: request profil pendaftaran di-`catchError`, jadi saat ia gagal nilai
   * ini `false` dan tombol HILANG untuk vendor yang sebenarnya berhak. Lebih baik tombol hilang
   * daripada tombol yang pasti berujung 400; error-nya sendiri sudah ditoast interceptor.
   *
   * Kembaran `ProfileEditStore.hasSlk` (situs admin) — aturannya satu, jangan diturunkan ulang.
   */
  readonly hasSlk = computed(() => this.slkId() !== null);

  /**
   * `true` setelah `reload()` shell SELESAI — sukses maupun gagal.
   *
   * Ada khusus supaya pembaca bisa membedakan "sedang dimuat" dari "memang tidak ada". `profile()`
   * tidak bisa dipakai untuk itu: ia juga `null` saat fetch gagal. Tanpa signal ini, tab Riwayat
   * Komentar menuduh vendor "belum mengajukan pendaftaran" selama sepersekian detik pertama setiap
   * kali dibuka.
   */
  readonly loaded = this.loadedFlag.asReadonly();

  /**
   * Tombol **Ajukan Pengajuan**. Tombolnya dirender tab Dokumen Perusahaan (di sebelah angka
   * kelengkapan), tetapi syarat dan dialognya tetap milik shell: status PDP dan `reload()` hidup
   * di sana. Arah kebalikan dari `SectionActionsStore` — di sini shell yang MENDEKLARASIKAN, tab
   * yang MERENDER.
   *
   * `null` sampai shell mendaftarkannya.
   */
  readonly submitAction = this.submit.asReadonly();

  /**
   * Pengajuan **sedang ditelaah tim internal** → tab Dokumen Perusahaan mengganti kartu progres
   * dengan banner "Menunggu Persetujuan". Terjemahan langsung dari acuan, atas `workflow_instance`
   * dari `GET /api/vendor/registration/profiles/current_user`:
   *
   * ```coffee
   * not isFormSubmission() and not isWorkflowNotStarted()
   * ```
   *
   * yaitu alurnya SUDAH dimulai dan langkah yang berjalan BUKAN "Form Submission". Aturan yang
   * sama dengan `ProfileEditStore.cannotSubmit` (yang mengunci aksi di halaman "Perbarui
   * Dokumen") — kalau salah satunya berubah, keduanya harus berubah.
   *
   * Satu beda yang disengaja: saat response belum/gagal tiba hasilnya `false`, bukan `true`.
   * `cannotSubmit` menahan tombol selama keadaannya belum diketahui; banner ini MENYATAKAN
   * sesuatu, dan menyatakan "sedang diverifikasi" tanpa tahu adalah salah.
   */
  readonly awaitingApproval = computed(() => {
    const workflow = this.workflow();
    if (workflow === undefined) return false;
    return (
      !isCurrentWorkflowStepName(workflow, FORM_SUBMISSION_STEP_NAME) && !isWorkflowEmpty(workflow)
    );
  });

  /**
   * Tombol **Ajukan Pengajuan** DITAMPILKAN (instruksi user 8 Okt 2026):
   *
   * ```coffee
   * isFormSubmission() or isWorkflowNotStarted()
   * ```
   *
   * yaitu langkah berjalan adalah "Form Submission" ATAU alurnya belum dimulai. Kebalikan persis
   * dari `awaitingApproval` — bedanya hanya saat response belum/gagal tiba: di sini `false`, supaya
   * tombol tidak berkedip. Mati/aktifnya tombol urusan lain (`SubmitAction.disabled`).
   */
  readonly submitVisible = computed(() => {
    const workflow = this.workflow();
    if (workflow === undefined) return false;
    return (
      isCurrentWorkflowStepName(workflow, FORM_SUBMISSION_STEP_NAME) || isWorkflowEmpty(workflow)
    );
  });

  /**
   * Riwayat langkah workflow pendaftaran vendor ini — `items` dari
   * `GET /api/workflow/workflow_instance_logs` (`vendor_registration_form_id`,
   * `enable_paging=false`, `created_at` asc), urutan kiriman BE apa adanya.
   *
   * Dimuat SEKALI oleh shell dan dibaca dua pihak: `revision` di bawah, dan tabel tab Riwayat
   * Komentar — yang karena itu tidak memanggil endpointnya sendiri.
   *
   * Kosong punya tiga arti (belum dimuat, gagal, memang tidak ada); pembedanya `loaded`.
   */
  readonly workflowLogs = this.logs.asReadonly();

  /**
   * Pengajuan **dikembalikan reviewer** → tab Dokumen Perusahaan menampilkan banner "Perlu
   * Revisi" dan kartu progres berganti label (Perbaiki / Ajukan Ulang). `null` = tidak sedang
   * dikembalikan.
   *
   * Aturannya (instruksi user 6 Okt 2026): item TERAKHIR dari
   * `GET /api/workflow/workflow_instance_logs?…&sort_info=created_at asc` ber-`system_desc`
   * "Workflow restarted". `comment` item yang sama adalah catatan reviewer.
   *
   * ⚠️ Urutannya urutan KIRIMAN BE, sengaja TIDAK diurutkan ulang dengan
   * `sortWorkflowLogsChronologically`: aturannya didefinisikan atas response itu apa adanya, dan
   * tie-break `id` di util tersebut bisa memindahkan item terakhir bila dua log lahir pada detik
   * yang sama.
   *
   * Log kosong (belum/gagal dimuat, atau vendor tanpa formulir) → `null`, banner tidak muncul.
   */
  readonly revision = computed<{ readonly comment: string | null } | null>(() => {
    const last = this.logs().at(-1);
    if (last?.system_desc !== WORKFLOW_RESTARTED_DESC) return null;
    return { comment: last.comment?.trim() || null };
  });

  set(value: VendorProfileData | null): void {
    this.data.set(value);
  }

  /** Terima `data` response apa adanya supaya total & items tidak bisa terpisah/basi sendiri. */
  setExpiringCreds(value: ExpiringCredListResponse['data'] | null): void {
    this.creds.set(value?.items ?? []);
    this.credTotal.set(value?.total_server_items ?? 0);
  }

  setRegistrationFormId(value: string | null): void {
    this.formId.set(value);
  }

  /** Disimpan sebagai id, bukan boolean: id-nya bisa dibutuhkan nanti dan boolean menutup pintu itu. */
  setSlkId(value: string | null): void {
    this.slkId.set(value);
  }

  setWorkflow(value: ProfileWorkflowInstance | null | undefined): void {
    this.workflow.set(value);
  }

  /** `items` response apa adanya — urutannya ikut dibaca, lihat `revision`. */
  setWorkflowLogs(value: readonly WorkflowInstanceLog[]): void {
    this.logs.set(value);
  }

  setSubmitAction(value: SubmitAction | null): void {
    this.submit.set(value);
  }

  setLoaded(value: boolean): void {
    this.loadedFlag.set(value);
  }
}
